import { omit, orderBy, pick } from 'lodash'

import { HASH_DELIMITER } from './const'
import {
  AssetTreeData,
  AssetTreeItem,
  AssetTreeNodeDescriptionGetter,
  AssetTreeNodeFormatter,
  AssetTreeSearchEntry,
} from './types'

/*
 * Every node carries these as siblings of its child keys — matches the legacy `UITree`'s own
 * `meta-fields="['amount', 'balance', 'path']"` (`AppTree.vue`). Missing `path` here makes it
 * look like a child entry and recurses into it, which is unbounded (its value is an array that
 * keeps growing a level further down) — that's the "Maximum call stack size exceeded" failure
 * mode if a field is ever added here without updating this list.
 */
const META_FIELDS = ['amount', 'balance', 'path'] as const

/**
 * `AnalyticsPreparedTree` is a nested object with meta fields (amount/balance/path) mixed into
 * the same level as child keys. This walks it once into Reka TreeRoot's flat
 * `{ key, children }` item shape, building the search index in the same pass — replacing the
 * legacy hash-the-raw-tree-then-rebuild-a-pruned-copy approach with a single build.
 */
export const buildAssetTree = (
  data: AssetTreeData,
  rootKey: string,
  rootName: string,
  formatter: AssetTreeNodeFormatter,
  getDescription?: AssetTreeNodeDescriptionGetter,
): { root?: AssetTreeItem, searchIndex: AssetTreeSearchEntry[], isLastChildByKey: Map<string, boolean> } => {
  const searchIndex: AssetTreeSearchEntry[] = []
  const isLastChildByKey = new Map<string, boolean>()

  const buildNode = (path: string[], node: AssetTreeData, isRoot: boolean): AssetTreeItem => {
    const key = path.join(HASH_DELIMITER)
    const meta = pick(node, META_FIELDS)
    const childEntries = omit(node, META_FIELDS)

    const name = isRoot ? rootName : formatter(path.at(-1) as string)
    const rawDescription = isRoot ? undefined : getDescription?.(path.at(-1) as string)
    const description = rawDescription && rawDescription !== name ? rawDescription : undefined

    if (!isRoot) {
      searchIndex.push({
        key,
        path,
        text: `${name}${description ? ` ${description}` : ''}`.toLowerCase(),
      })
    }

    // Sort before recursing so searchIndex and tree order match.
    const sortedChildKeys = orderBy(Object.keys(childEntries), childKey => (childEntries[childKey] as AssetTreeData)?.balance, 'desc')
    const children = sortedChildKeys.length
      ? sortedChildKeys.map(childKey =>
        buildNode([...path, childKey], childEntries[childKey] as AssetTreeData, false))
      : undefined

    children?.forEach((child, index) => {
      isLastChildByKey.set(child.key, index === children.length - 1)
    })

    return { key, name, description, path, amount: meta.amount, balance: meta.balance, children }
  }

  const rootNode = data[rootKey]
  if (!rootNode) return { searchIndex: [], isLastChildByKey }
  return { root: buildNode([rootKey], rootNode, true), searchIndex, isLastChildByKey }
}

const collectAncestorKeys = (path: string[]): string[] =>
  path.slice(0, -1).map((_, index) => path.slice(0, index + 1).join(HASH_DELIMITER))

/**
 * Matched keys plus every ancestor of a match, so a hit surfaces with its full branch
 * expanded — same intent as the legacy hashed-search approach, computed over the already-built
 * item tree instead of re-walking raw data with lodash `get`/`set` paths.
 */
export const matchAssetTreeSearch = (searchIndex: AssetTreeSearchEntry[], query: string) => {
  const needle = query.trim().toLowerCase()
  const matchedKeys: string[] = []
  const visibleKeys = new Set<string>()
  const expandKeys = new Set<string>()

  if (!needle) return { matchedKeys, visibleKeys, expandKeys }

  searchIndex.forEach(entry => {
    if (!entry.text.includes(needle)) return
    matchedKeys.push(entry.key)
    visibleKeys.add(entry.key)
    collectAncestorKeys(entry.path).forEach(ancestorKey => {
      visibleKeys.add(ancestorKey)
      expandKeys.add(ancestorKey)
    })
  })

  return { matchedKeys, visibleKeys, expandKeys }
}

/** Keeps the root node, drops any child (at any depth) not in `visibleKeys`. */
export const pruneAssetTreeForSearch = (item: AssetTreeItem, visibleKeys: Set<string>): AssetTreeItem => ({
  ...item,
  children: item.children
    ?.filter(child => visibleKeys.has(child.key))
    .map(child => pruneAssetTreeForSearch(child, visibleKeys)),
})
